/**
 * Microphone setup — latency check + mic selection with level meter + Ready button.
 * Rendered inside the mic-setup phase of GamePlayer.
 */

import { useMicrophone } from "../hooks/useMicrophone";
import type { LatencyResult } from "../lib/latency";

interface MicSetupProps {
  onReady: (deviceId: string, cleanupMic: () => void) => void;
  locationsReady: boolean;
  bestRegion: LatencyResult | null;
  /** True once the Cloudflare Turnstile challenge has issued a token */
  humanVerified: boolean;
}

export function MicSetup({ onReady, locationsReady, bestRegion, humanVerified }: MicSetupProps) {
  const { devices, selectedId, setSelectedId, level, isReady, hasDetectedAudio, permissionGranted, cleanup } =
    useMicrophone();

  const latencyOk = bestRegion && bestRegion.latency < 50;

  return (
    <>
      <div className="pp-status">
        {!locationsReady ? (
          <span className="pp-mono-lines">Checking connection...</span>
        ) : latencyOk ? (
          <>
            <div className="pp-dot dot-green" />
            <span>{bestRegion.latency.toFixed(0)}ms latency</span>
          </>
        ) : (
          <>
            <div className="pp-dot dot-red" />
            <span>{bestRegion!.latency.toFixed(0)}ms latency — may be too slow</span>
          </>
        )}
      </div>

      <div className="pp-headline">Microphone setup</div>

      <div className="pp-device">
        {!permissionGranted ? (
          <div className="pp-device-name" style={{ textAlign: "center", whiteSpace: "normal", overflow: "visible" }}>
            Please accept your browser microphone permission.
          </div>
        ) : devices.length === 0 ? (
          <>
            <div className="pp-device-name err" style={{ textAlign: "center", whiteSpace: "normal", overflow: "visible" }}>
              No microphones found
            </div>
            <div className="pp-device-sub" style={{ textAlign: "center" }}>
              Connect a microphone and refresh.
            </div>
          </>
        ) : (
          <>
            {devices.length > 1 ? (
              <select
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
                className="pp-device-select"
              >
                {devices.map((device) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label || `Microphone ${device.deviceId.slice(0, 8)}`}
                  </option>
                ))}
              </select>
            ) : (
              <div className="pp-device-name">
                {devices[0]?.label || "Microphone"}
              </div>
            )}
            <div className="pp-track">
              <div className="pp-fill" style={{ width: `${level}%` }} />
            </div>
          </>
        )}
      </div>

      <button
        className={`pp-btn ${!isReady || !locationsReady || !hasDetectedAudio || !humanVerified ? "dim" : ""}`}
        disabled={!isReady || !locationsReady || !hasDetectedAudio || !humanVerified}
        onClick={() => onReady(selectedId, cleanup)}
      >
        {!locationsReady
          ? "Measuring latency..."
          : !hasDetectedAudio
            ? "Speak to test..."
            : !humanVerified
              ? "Verifying..."
              : "Ready"}
      </button>
    </>
  );
}
